import type { Ref } from 'react';
import { useEffect, useRef } from 'react';
import type { HTMLAttributes, RefObject } from 'react';
import { cx } from '../lib/cx';
import { mergeRefs } from '../lib/merge-refs';

const MIN_THUMB_LENGTH = 32;

export type ScrollAxis = 'y' | 'x';

const AXES = {
  y: { scroll: 'scrollTop', content: 'scrollHeight', client: 'clientHeight', length: 'height', translate: 'translateY', pointer: 'clientY' },
  x: { scroll: 'scrollLeft', content: 'scrollWidth', client: 'clientWidth', length: 'width', translate: 'translateX', pointer: 'clientX' },
} as const;

export interface ScrollBarProps extends HTMLAttributes<HTMLDivElement> {
  scrollerRef: RefObject<HTMLElement | null>;
  axis?: ScrollAxis;
}

export function ScrollBar(
  { ref, scrollerRef, axis = 'y', className, ...rest }: ScrollBarProps & { ref?: Ref<HTMLDivElement> },
) {
  const barRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const scroller = scrollerRef.current;
    const scrollbar = barRef.current;
    const thumb = thumbRef.current;
    if (!scroller || !scrollbar || !thumb) return;
    const a = AXES[axis];
    let dragStart = 0;
    let dragStartScroll = 0;
    let dragging = false;
    const geometry = () => {
      const track = scrollbar[a.client];
      const length = Math.max(MIN_THUMB_LENGTH, (scroller[a.client] / scroller[a.content]) * track);
      return { length, travel: track - length };
    };
    const update = () => {
      const content = scroller[a.content];
      const client = scroller[a.client];
      if (content <= client + 1) {
        scrollbar.dataset.empty = '';
        return;
      }
      delete scrollbar.dataset.empty;
      const { length, travel } = geometry();
      const fraction = scroller[a.scroll] / (content - client);
      const rtl = axis === 'x' && getComputedStyle(scroller).direction === 'rtl';
      thumb.style[a.length] = `${length}px`;
      thumb.style.transform = `${a.translate}(${(rtl ? 1 + fraction : fraction) * travel}px)`;
    };
    scroller.addEventListener('scroll', update, { passive: true });
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    ro?.observe(scroller);
    const mo = typeof MutationObserver !== 'undefined' ? new MutationObserver(update) : null;
    mo?.observe(scroller, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style'],
    });
    const onDown = (e: PointerEvent) => {
      e.preventDefault();
      dragging = true;
      dragStart = e[a.pointer];
      dragStartScroll = scroller[a.scroll];
      thumb.classList.add('is-dragging');
      thumb.setPointerCapture(e.pointerId);
    };
    const onMove = (e: PointerEvent) => {
      if (!dragging) return;
      const ratio = (scroller[a.content] - scroller[a.client]) / geometry().travel;
      scroller[a.scroll] = dragStartScroll + (e[a.pointer] - dragStart) * ratio;
    };
    const stop = (e: PointerEvent) => {
      if (!dragging) return;
      dragging = false;
      thumb.classList.remove('is-dragging');
      try {
        thumb.releasePointerCapture(e.pointerId);
      } catch (error) { void error }
    };
    thumb.addEventListener('pointerdown', onDown);
    thumb.addEventListener('pointermove', onMove);
    thumb.addEventListener('pointerup', stop);
    thumb.addEventListener('pointercancel', stop);
    update();
    return () => {
      scroller.removeEventListener('scroll', update);
      ro?.disconnect();
      mo?.disconnect();
      thumb.removeEventListener('pointerdown', onDown);
      thumb.removeEventListener('pointermove', onMove);
      thumb.removeEventListener('pointerup', stop);
      thumb.removeEventListener('pointercancel', stop);
    };
  }, [scrollerRef, barRef, thumbRef, axis]);
  return (
    <div
      ref={mergeRefs(barRef, ref)}
      className={cx('scroll-bar', axis === 'x' && 'scroll-bar--x', className)}
      data-empty
      {...rest}
    >
      <div className="scroll-bar-thumb" ref={thumbRef} />
    </div>
  );
}
